import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Autocomplete,
  Grid,
  Paper,
  Divider,
  FormHelperText,
} from "@mui/material";
import { EntraUser } from "../../../../../services/profileService/model/entraUser/EntraUser";
import { useUserSearchQuery } from "../../../../../services/profileService/profile-api";
import { FacilitySelector } from "../../../components/facilitySelector/FacilitySelector";
import { UARUserSelector } from "./UARUserSelector";

interface CPSIFields {
  newUser: EntraUser;
  userToCopy: EntraUser | null;
  locations: string[];
}

interface CPSITicketModuleProps {
  updateCPSIFields: (fields: CPSIFields) => void;
}

export const CPSITicketModule: React.FC<CPSITicketModuleProps> = ({
  updateCPSIFields,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [userToCopy, setUserToCopy] = useState<EntraUser | null>(null);
  const [newUser, setNewUser] = useState<EntraUser>(new EntraUser());
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  // Validation states
  const [userToCopyTouched, setUserToCopyTouched] = useState(false);
  const [newUserTouched, setNewUserTouched] = useState(false);
  const [locationsTouched, setLocationsTouched] = useState(false);
  // Error states
  const userToCopyError = userToCopyTouched && userToCopy === null;
  const newUserError = newUserTouched && (!newUser.displayName || !newUser.mail);
  const locationsError =
    locationsTouched && (!selectedLocations || selectedLocations.length === 0);

  const { data } = useUserSearchQuery(searchTerm, {
    skip: searchTerm.length < 2 || selectedLocations.length === 0,
  });

  // Filter users based on selected facilities
  const filteredUsers = (data ?? []).filter((user) => {
    if (selectedLocations.length === 0) return true;

    return selectedLocations.some((facilityName) => {
      const facilityLower = facilityName.toLowerCase();
      const departmentMatch = user.department?.toLowerCase().includes(facilityLower);
      const companyMatch = user.companyName?.toLowerCase().includes(facilityLower);
      return departmentMatch || companyMatch;
    });
  });

  const users = [...filteredUsers];
  const handleUserToCopyChange = (user: EntraUser | null) => {
    setUserToCopy(user);
    setUserToCopyTouched(true);
    updateCPSIFields({
      newUser: newUser,
      userToCopy: user,
      locations: selectedLocations,
    });
  };

  const handleNewUserChange = (user: EntraUser) => {
    setNewUser(user);
    setNewUserTouched(true);
    updateCPSIFields({
      newUser: user,
      userToCopy: userToCopy,
      locations: selectedLocations,
    });
  };
  const handleLocationsChange = (locations: string[]) => {
    setSelectedLocations(locations);
    setLocationsTouched(true);
    updateCPSIFields({
      newUser: newUser,
      userToCopy: userToCopy,
      locations: locations,
    });
  };  // Mark all fields as touched when form is submitted
  useEffect(() => {    const markFieldsAsTouched = () => {
      setUserToCopyTouched(true);
      setNewUserTouched(true);
      setLocationsTouched(true);
    };

    // Find the closest form element and add submit listener
    const form = document.querySelector("form");
    if (form) {
      form.addEventListener("submit", markFieldsAsTouched);
      return () => form.removeEventListener("submit", markFieldsAsTouched);
    }
  }, []);

  return (
    <Paper
      elevation={2}
      sx={{ p: 3, borderRadius: 2, backgroundColor: "#fcfcfc" }}
    >
      <Typography
        variant="h6"
        sx={{ mb: 3, color: "#2c3e50", fontWeight: 600 }}
      >
        CPSI Access Request Details
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
        <Grid item xs={12}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 1.5, fontWeight: 500, color: "#34495e" }}
            >
              New User <span style={{ color: "red" }}>*</span>
            </Typography>
            <Box sx={{ flex: 1, '& .MuiInputBase-root': { minHeight: 40, mb: 0 } }}>
              <UARUserSelector 
                value={newUser?.displayName || ""}
                updateFunc={handleNewUserChange}
              />
              <FormHelperText error={!!newUserError}>
                {newUserError ? 'New user information is required' : '\u00A0'}
              </FormHelperText>
            </Box>
          </Box>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 1.5, fontWeight: 500, color: "#34495e" }}
            >
              Facility <span style={{ color: "red" }}>*</span>
            </Typography>
            <Box sx={{ flex: 1, '& .MuiInputBase-root': { minHeight: 40, mb: 0 } }}>
              <FacilitySelector
                selectedLocations={selectedLocations}
                onChange={handleLocationsChange}
                ticketType="CPSI"
              />
              <FormHelperText error={!!locationsError}>
                {locationsError ? 'Facility is required' : '\u00A0'}
              </FormHelperText>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 1.5, fontWeight: 500, color: "#34495e" }}
            >
              Copy Permissions From User <span style={{ color: "red" }}>*</span>
            </Typography>
            <Box sx={{ flex: 1, '& .MuiInputBase-root': { minHeight: 40, mb: 0 } }}>
              <Autocomplete
                options={users}
                getOptionLabel={(user) => user.displayName || ""}
                value={userToCopy}
                onInputChange={(e, value) => setSearchTerm(value)}
                onChange={(e, value) => handleUserToCopyChange(value)}
                disabled={selectedLocations.length === 0}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="copy-permissions-user-autocomplete"
                    name="copyPermissionsUser"
                    variant="outlined"
                    size="small"
                    margin="none"
                    placeholder={
                      selectedLocations.length === 0
                        ? "Please select a facility first"
                        : "Search for a user"
                    }
                    error={userToCopyError}
                    required
                  />
                )}
                noOptionsText="No users found"
              />
              <FormHelperText error={!!userToCopyError}>
                {userToCopyError
                  ? 'User is required'
                  : selectedLocations.length === 0
                  ? 'Select a facility to enable user search'
                  : '\u00A0'}
              </FormHelperText>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};
